import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type { DocumentDTO } from '@almanac/shared';
import {
  expiryStatus,
  formatExpiry,
  isExpiryHandled,
  isRetiredRecord,
  isVaultRecord,
  useUpdateDocument,
  vaultNextReview,
  vaultReviewDue,
} from '../lib/documents.js';
import { formatFileSize } from '../lib/files.js';
import { useCreateObligation, useDeleteObligation, useNowMinute } from '../lib/obligations.js';
import { usePeople } from '../lib/people.js';
import { useUndoToast } from '../lib/undoToast.js';
import { FileViewer } from './FileViewer.js';
import { PersonAvatar } from './PersonAvatar.js';
import { PersonCard } from './PersonCard.js';

interface Props {
  document: DocumentDTO;
  lifeAreaName?: string;
  onClose: () => void;
  /** Called when the Edit action is taken. The page closes this preview
   *  first, then opens the full edit drawer for the same id. */
  onEdit: () => void;
}

/**
 * Read-first preview of a document. Opens as a centered card with a
 * backdrop scrim, distinct from the edit drawer (a bottom sheet) so the
 * two read as different surfaces — quick-look vs. full edit. Tapping a
 * document row opens *this*, not the form: the common intent is to look
 * at the document, not change its metadata.
 *
 * Most fields are read-only facts. The exceptions, mirroring the
 * obligation preview, are notes (an inline textarea that saves on blur)
 * and the file location (shown prominently with a Copy action — opening
 * the actual file lands with the upload/serving work).
 *
 * `doc` is the document; the prop is named `document` for the call site
 * but aliased here so it doesn't shadow the global `document`.
 */
export function DocumentPreview({ document: doc, lifeAreaName, onClose, onEdit }: Props) {
  const navigate = useNavigate();
  const update = useUpdateDocument();
  const people = usePeople();
  const nowMs = useNowMinute();
  const now = new Date(nowMs);
  const [copied, setCopied] = useState(false);
  const [viewing, setViewing] = useState(false);
  // A person opened from an "About" chip — the shared person card,
  // stacked one layer above this document card.
  const [viewingPersonId, setViewingPersonId] = useState<string | null>(null);

  const isShared = doc.visibility === 'shared';
  const status = expiryStatus(doc, now);
  const expiryLabel = formatExpiry(doc, now);

  // ── Expiry follow-through ─────────────────────────────
  // Recognition alone isn't enough: an expiring document either becomes
  // a renewal obligation (the tool whose job is nagging) or is
  // dismissed for this expiry date. Both quiet the attention surfaces
  // (Today card, index banner, counts); the red date stays on rows and
  // in the expiry view. The dismissal names the expiry date it applies
  // to, so a renewal that updates the date re-arms attention by itself.
  const createObligation = useCreateObligation();
  const deleteObligation = useDeleteObligation();
  const undo = useUndoToast();
  const handled = isExpiryHandled(doc);
  const expiryKey = doc.expiryDate?.slice(0, 10) ?? null;

  function dismissExpiry() {
    if (!expiryKey) return;
    update.mutate({ id: doc.id, input: { expiryDismissedFor: expiryKey } });
    undo.show('Expiry reminder dismissed', () => {
      update.mutate({ id: doc.id, input: { expiryDismissedFor: null } });
    });
  }

  function remindAgain() {
    update.mutate({ id: doc.id, input: { expiryDismissedFor: null } });
    undo.show('Expiry reminder back on', () => {
      if (expiryKey) update.mutate({ id: doc.id, input: { expiryDismissedFor: expiryKey } });
    });
  }

  // ── Vault membership & annual review ──────────────────
  // A vault record is the same document with documentCategory 'vital';
  // 'retired' keeps it in the Vault's Retired drawer. The card is where
  // membership flips happen (quiet links in the timestamps block) and
  // where the annual review is answered. Dropbox copies follow the
  // category server-side.
  const inVault = isVaultRecord(doc);
  const retired = isRetiredRecord(doc);
  const reviewDue = vaultReviewDue(doc, now);

  function setCategoryWithUndo(
    next: 'routine' | 'vital' | 'retired',
    message: string,
  ) {
    const previous = doc.documentCategory;
    update.mutate({ id: doc.id, input: { documentCategory: next } });
    undo.show(message, () => {
      update.mutate({ id: doc.id, input: { documentCategory: previous } });
    });
  }

  function confirmCurrent() {
    const previous = doc.vaultReviewedAt;
    update.mutate({ id: doc.id, input: { vaultReviewedAt: new Date().toISOString() } });
    undo.show('Confirmed current for another year', () => {
      update.mutate({ id: doc.id, input: { vaultReviewedAt: previous ?? null } });
    });
  }

  function addRenewalTask() {
    if (!expiryKey) return;
    // Due a month before expiry — enough lead for passports-by-post
    // territory — clamped to today when the date is closer than that
    // (or already past).
    const lead = new Date(`${expiryKey}T00:00:00`);
    lead.setDate(lead.getDate() - 30);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const due = lead < today ? today : lead;
    const pad = (n: number) => (n < 10 ? `0${n}` : String(n));
    const dueIso = `${due.getFullYear()}-${pad(due.getMonth() + 1)}-${pad(due.getDate())}`;
    const obligationId = crypto.randomUUID();
    createObligation.mutate({
      id: obligationId,
      title: `Renew ${doc.title}`,
      description: `${doc.title} expires ${formatDate(expiryKey)}.`,
      lifeAreaId: doc.lifeAreaId,
      dueDate: dueIso,
      recurrenceRule: null,
      priority: 'medium',
      reminderDaysBefore: null,
      notes: null,
      visibility: doc.visibility,
      kind: 'task',
      occasionType: null,
      occasionLabel: null,
      anchorYear: null,
      personIds: doc.pertainsTo ?? [],
    });
    update.mutate({ id: doc.id, input: { expiryDismissedFor: expiryKey } });
    undo.show(`Added “Renew ${doc.title}” to Obligations`, () => {
      deleteObligation.mutate({ id: obligationId });
      update.mutate({ id: doc.id, input: { expiryDismissedFor: null } });
    });
  }

  const aboutPeople = (doc.pertainsTo ?? [])
    .map((id) => people.data?.find((p) => p.id === id))
    .filter((p): p is NonNullable<typeof p> => Boolean(p));

  // Inline-editable notes — quick jots shouldn't need the full drawer.
  // Held locally and flushed on blur / any dismiss path.
  const [notesDraft, setNotesDraft] = useState(doc.notes ?? '');
  const notesRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    setNotesDraft(doc.notes ?? '');
  }, [doc.id, doc.notes]);

  useEffect(() => {
    const el = notesRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${el.scrollHeight}px`;
  }, [notesDraft]);

  function commitNotes() {
    const next = notesDraft.trim();
    const current = (doc.notes ?? '').trim();
    if (next === current) return;
    update.mutate({ id: doc.id, input: { notes: next || null } });
  }

  function closeWithSave() {
    commitNotes();
    onClose();
  }

  function handleEdit() {
    commitNotes();
    onEdit();
  }

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') closeWithSave();
    }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
    // closeWithSave reads the latest notesDraft via closure each render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [notesDraft, doc.id]);

  function copyPath() {
    const path = doc.dropboxPath;
    if (!path) return;
    void navigator.clipboard?.writeText(path).then(() => {
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1600);
    });
  }

  function openFile() {
    commitNotes();
    // Render the file in an in-app viewer rather than `window.open` —
    // a `_blank` navigation in an iOS standalone PWA opens a blank,
    // un-closeable in-app view (FileViewer.tsx explains the fix).
    setViewing(true);
  }

  // A tag chip filters the Documents view to everything carrying it.
  // Close-then-navigate so the card doesn't linger over the results.
  function openTag(tag: string) {
    commitNotes();
    onClose();
    navigate(`/documents?tag=${encodeURIComponent(tag)}`);
  }

  const hasFile = Boolean(doc.fileName);

  const subtitleParts = [doc.category, lifeAreaName, doc.subArea].filter(
    (p): p is string => Boolean(p),
  );

  return (
    <>
      <div className="preview-scrim" onClick={closeWithSave} aria-hidden />
      <div
        className="preview-card"
        role="dialog"
        aria-modal="true"
        aria-label={`Details for ${doc.title}`}
      >
        <button
          type="button"
          className="preview-card__close"
          onClick={closeWithSave}
          aria-label="Close"
        >
          ×
        </button>

        <div className="preview-card__header">
          <div className="doc-title-row">
            <h2 className="preview-card__title">{doc.title}</h2>
            {/* Key-document star: one tap flags this as one of the few
                that matter (passport, vaccination record). Starred
                documents surface on the person card of everyone the
                document is about. */}
            <button
              type="button"
              className={`doc-star ${doc.starred ? 'doc-star--on' : ''}`}
              onClick={() => update.mutate({ id: doc.id, input: { starred: !doc.starred } })}
              aria-label={doc.starred ? 'Unmark as key document' : 'Mark as key document'}
              aria-pressed={doc.starred}
              title={doc.starred ? 'Key document' : 'Mark as key document'}
            >
              {doc.starred ? (
                <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" aria-hidden>
                  <path d="M12 2l2.9 6.26 6.6.7-4.9 4.5 1.35 6.54L12 16.9 6.05 20l1.35-6.54-4.9-4.5 6.6-.7z" />
                </svg>
              ) : (
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.6"
                  strokeLinejoin="round"
                  aria-hidden
                >
                  <path d="M12 2l2.9 6.26 6.6.7-4.9 4.5 1.35 6.54L12 16.9 6.05 20l1.35-6.54-4.9-4.5 6.6-.7z" />
                </svg>
              )}
            </button>
          </div>
          <div className="preview-card__subtitle">
            {subtitleParts.join(' · ')}
            {isShared && (
              <>
                {subtitleParts.length > 0 && ' '}
                <span className="shared-pill">Shared</span>
              </>
            )}
          </div>
        </div>

        <div className="preview-card__facts">
          {aboutPeople.length > 0 && (
            <div className="preview-card__fact">
              <span className="preview-card__fact-label">About</span>
              {/* Tappable person chips — the document's people open the
                  shared person card, same as everywhere else. */}
              <span className="preview-card__fact-value preview-card__fact-value--people">
                {aboutPeople.map((p) => (
                  <button
                    key={p.id}
                    type="button"
                    className="person-link"
                    onClick={() => setViewingPersonId(p.id)}
                  >
                    {p.avatarVersion && <PersonAvatar person={p} className="person-link__av" />}
                    {p.displayName}
                  </button>
                ))}
              </span>
            </div>
          )}
          {doc.issuer && <FactLine label="Issuer" value={doc.issuer} />}
          {doc.issuedDate && <FactLine label="Issued" value={formatDate(doc.issuedDate)} />}
          {expiryLabel && (
            <FactLine
              label="Expiry"
              value={expiryLabel}
              emphasize={status === 'expired' || status === 'soon'}
            />
          )}
          {lifeAreaName && <FactLine label="Life area" value={lifeAreaName} />}
          {inVault && (
            <FactLine
              label="Reviewed"
              value={reviewFactValue(doc, reviewDue)}
              emphasize={reviewDue}
            />
          )}
          {doc.tags && doc.tags.length > 0 && (
            <div className="preview-card__fact">
              <span className="preview-card__fact-label">Tags</span>
              {/* Tappable: each tag filters the Documents view to
                  everything carrying it — the way tags earn their keep. */}
              <span className="preview-card__fact-value preview-card__fact-value--people">
                {doc.tags.map((t) => (
                  <button
                    key={t}
                    type="button"
                    className="tag-link"
                    onClick={() => openTag(t)}
                  >
                    #{t}
                  </button>
                ))}
              </span>
            </div>
          )}
        </div>

        {expiryKey && (status === 'expired' || status === 'soon') && (
          handled ? (
            <div className="preview-expiry preview-expiry--handled">
              <span className="preview-expiry__text">
                Noted — expiry reminders are off for this date.
              </span>
              <span className="preview-expiry__actions">
                <button
                  type="button"
                  className="btn btn--secondary btn--small"
                  onClick={remindAgain}
                >
                  Remind again
                </button>
              </span>
            </div>
          ) : (
            <div className="preview-expiry">
              <span className="preview-expiry__text">
                {status === 'expired' ? 'This document has expired.' : 'This document is expiring soon.'}
              </span>
              <span className="preview-expiry__actions">
                <button
                  type="button"
                  className="btn btn--secondary btn--small"
                  onClick={dismissExpiry}
                >
                  Dismiss
                </button>
                <button
                  type="button"
                  className="btn btn--primary btn--small"
                  onClick={addRenewalTask}
                >
                  Add renewal task
                </button>
              </span>
            </div>
          )
        )}

        {inVault && reviewDue && (
          <div className="preview-expiry">
            <span className="preview-expiry__text">
              Annual review — is this record still current?
            </span>
            <span className="preview-expiry__actions">
              <button
                type="button"
                className="btn btn--secondary btn--small"
                onClick={handleEdit}
              >
                Needs update
              </button>
              <button
                type="button"
                className="btn btn--primary btn--small"
                onClick={confirmCurrent}
              >
                Still current
              </button>
            </span>
          </div>
        )}

        <div className="preview-card__file">
          <div className="preview-card__section-label">File</div>
          {hasFile ? (
            <button type="button" className="preview-card__file-open" onClick={openFile}>
              <span className="preview-card__file-open-icon" aria-hidden>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                  <path
                    d="M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8z"
                    stroke="currentColor"
                    strokeWidth="1.8"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M14 3v5h5"
                    stroke="currentColor"
                    strokeWidth="1.8"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </span>
              <span className="preview-card__file-open-meta">
                <span className="preview-card__file-open-name">{doc.fileName}</span>
                <span className="preview-card__file-open-sub">
                  {doc.fileSize != null ? `${formatFileSize(doc.fileSize)} · ` : ''}Tap to open
                </span>
              </span>
            </button>
          ) : !doc.dropboxPath ? (
            <p className="preview-card__file-empty">No file attached yet. Add one via Edit.</p>
          ) : null}
        </div>

        <textarea
          ref={notesRef}
          className="preview-card__notes preview-card__notes--editable"
          value={notesDraft}
          onChange={(e) => setNotesDraft(e.target.value)}
          onBlur={commitNotes}
          placeholder="Add a note…"
          rows={1}
          aria-label="Notes"
        />

        {/* Auto-managed Dropbox location. The full path is long, so it's
            collapsed by default (an eyebrow + chevron) and expands to
            reveal the monospace path and a Copy action — kept out of the
            way of the facts the user actually reads. */}
        {doc.dropboxPath && (
          <details className="preview-card__dropbox">
            <summary className="preview-card__dropbox-summary">
              <span className="preview-card__dropbox-label">In Dropbox</span>
              <span className="preview-card__dropbox-hint">Show file path</span>
              <svg
                className="preview-card__dropbox-chevron"
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="none"
                aria-hidden
              >
                <path
                  d="M6 9l6 6 6-6"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </summary>
            <div className="preview-card__dropbox-body">
              <code className="preview-card__dropbox-path">{doc.dropboxPath}</code>
              <button
                type="button"
                className="preview-card__dropbox-copy"
                onClick={copyPath}
                title="Copy Dropbox path"
              >
                {copied ? 'Copied' : 'Copy'}
              </button>
            </div>
            {doc.dropboxVaultPath && (
              <div className="preview-card__dropbox-body">
                <code className="preview-card__dropbox-path">{doc.dropboxVaultPath}</code>
              </div>
            )}
          </details>
        )}

        <div className="preview-card__timestamps">
          <span>Added {formatStamp(doc.createdAt)}</span>
          <span>Last updated {formatStamp(doc.updatedAt)}</span>
          {/* Vault membership — quiet, reversible flips. Files follow
              on Dropbox server-side (copy under /Vault/, Retired
              records under /Vault/Retired/). */}
          <span>
            {inVault ? (
              <>
                In the Vault ·{' '}
                <button
                  type="button"
                  className="preview-card__quiet-action"
                  onClick={() => setCategoryWithUndo('retired', 'Moved to Retired')}
                >
                  Retire
                </button>{' '}
                ·{' '}
                <button
                  type="button"
                  className="preview-card__quiet-action"
                  onClick={() => setCategoryWithUndo('routine', 'Moved to Documents')}
                >
                  Move to Documents
                </button>
              </>
            ) : retired ? (
              <>
                Retired ·{' '}
                <button
                  type="button"
                  className="preview-card__quiet-action"
                  onClick={() => setCategoryWithUndo('vital', 'Returned to the Vault')}
                >
                  Return to Vault
                </button>
              </>
            ) : (
              <button
                type="button"
                className="preview-card__quiet-action"
                onClick={() => setCategoryWithUndo('vital', 'Moved to the Vault')}
              >
                Move to Vault
              </button>
            )}
          </span>
        </div>

        <footer className="preview-card__footer">
          <button type="button" className="btn btn--secondary btn--small" onClick={handleEdit}>
            Edit
          </button>
          {/* Terracotta primary action, mirroring the obligation card's
              "Mark complete". The document's primary verb is getting to
              the file — Open it (the Dropbox copy has its own copy action
              above), or a prompt to add one when none is attached. */}
          {hasFile ? (
            <button type="button" className="btn btn--primary btn--small" onClick={openFile}>
              Open
            </button>
          ) : (
            <button type="button" className="btn btn--primary btn--small" onClick={handleEdit}>
              Add file
            </button>
          )}
        </footer>
      </div>

      {viewing && hasFile && (
        <FileViewer
          documentId={doc.id}
          fileName={doc.fileName ?? 'Document'}
          mimeType={doc.mimeType}
          onClose={() => setViewing(false)}
        />
      )}

      {viewingPersonId && (
        <PersonCard personId={viewingPersonId} onClose={() => setViewingPersonId(null)} />
      )}
    </>
  );
}

function FactLine({
  label,
  value,
  emphasize,
}: {
  label: string;
  value: string;
  emphasize?: boolean;
}) {
  return (
    <div className="preview-card__fact">
      <span className="preview-card__fact-label">{label}</span>
      <span
        className={`preview-card__fact-value ${emphasize ? 'preview-card__fact-value--emphasize' : ''}`}
      >
        {value}
      </span>
    </div>
  );
}

/** "14 Jun 2025 · next due 14 Jun 2026" — or the due/never variants.
 *  The next-due date reflects the record's own cadence (6/12/24
 *  months), so a shortened passport review reads honestly here. */
function reviewFactValue(doc: DocumentDTO, reviewDue: boolean): string {
  const next = vaultNextReview(doc);
  const nextPart = next
    ? reviewDue
      ? ` · was due ${formatDate(next.toISOString())}`
      : ` · next due ${formatDate(next.toISOString())}`
    : '';
  return doc.vaultReviewedAt
    ? `${formatDate(doc.vaultReviewedAt)}${nextPart}`
    : `Not yet${nextPart}`;
}

function formatDate(iso: string): string {
  const d = new Date(`${iso.slice(0, 10)}T00:00:00`);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString(undefined, { day: 'numeric', month: 'long', year: 'numeric' });
}

function formatStamp(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });
}
